import apiClient from './api.service';

/**
 * Invitation Service
 * Handles all invitation-related API calls
 */

export interface Invitation {
  id: number;
  store_id: number;
  invited_user_id: number;
  role_name: string;
  role_names?: string[];
  message: string | null;
  status: 'pending' | 'accepted' | 'rejected';
  created_by: number;
  responded_at: string | null;
  created_at: string;
  updated_at: string;
  // Additional fields from joins
  invited_email?: string;
  invited_pseudo?: string;
  created_by_pseudo?: string;
  store_name?: string;
  store_description?: string;
  invited_by_pseudo?: string;
}

export interface CreateInvitationData {
  emailOrPseudo: string;
  roleNames: string[];
  message?: string;
}

class InvitationService {
  /**
   * Create a new invitation
   * @param storeId - Store ID
   * @param data - Invitation data
   * @returns Promise with created invitation
   */
  async createInvitation(storeId: number, data: CreateInvitationData): Promise<Invitation> {
    const response = await apiClient.post(`/stores/${storeId}/invitations`, data);
    return response.data.data.invitation;
  }

  /**
   * Get all invitations for a store
   * @param storeId - Store ID
   * @returns Promise with invitations list
   */
  async getStoreInvitations(storeId: number): Promise<Invitation[]> {
    const response = await apiClient.get(`/stores/${storeId}/invitations`);
    return response.data.data.invitations;
  }

  /**
   * Get pending invitations for current user
   * @returns Promise with pending invitations
   */
  async getPendingInvitations(): Promise<Invitation[]> {
    const response = await apiClient.get('/invitations/pending');
    return response.data.data.invitations;
  }

  /**
   * Accept an invitation
   * @param invitationId - Invitation ID
   * @returns Promise with invitation and storeId
   */
  async acceptInvitation(invitationId: number): Promise<{invitation: Invitation, storeId: number}> {
    const response = await apiClient.post(`/invitations/${invitationId}/accept`);
    return response.data.data;
  }

  /**
   * Reject an invitation
   * @param invitationId - Invitation ID
   * @returns Promise
   */
  async rejectInvitation(invitationId: number): Promise<void> {
    await apiClient.post(`/invitations/${invitationId}/reject`);
  }

  /**
   * Cancel an invitation (by store admin)
   * @param storeId - Store ID
   * @param invitationId - Invitation ID
   * @returns Promise
   */
  async cancelInvitation(storeId: number, invitationId: number): Promise<void> {
    await apiClient.delete(`/stores/${storeId}/invitations/${invitationId}`);
  }
}

export default new InvitationService();

